import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import PageNavHeader from '@/components/common/EnkiDummyUI/PageNavHeader';
import GlobalNavHeader from '@/components/common/GlobalNavHeader';
import Header from '@/components/common/HeaderNew';
import useGlobalNavHeader from '@/hooks/globalnavheader/provider';
import useScrollToAddBoxShadow from '@/hooks/useScrollToAddBoxShadow';
import useSticky from '@/hooks/useSticky';
import { isSayWeeeApp } from '@/utils/tool';
import useTheme from '@/store/theme';

const ENKI_DEFAULTS_TO_NEW_HEADER = true; // WHEN_NOT_EXPLICIT_BLOCKED and not inside native app etc
export interface Props {
  className?: string;
  style?: CSSProperties;
  headerWrapClassName?: string;
  headerClassName?: string;
  title?: string | React.ReactNode;
  contentClassName?: string;
  contentStyle?: CSSProperties;
  children?: ReactNode;
  onShare?: (any) => void;
  shareData?: ObjectType;
  onBack?: () => string;
  topNode?: React.ReactNode;
  header?: React.ReactNode | boolean;
  //是否默认微信分享信息
  isServerSide?: boolean;
  shareIcon?: string;
  shareAfterClose?: () => void;
  trackSharePopupClick?: (e) => void;
  onBeforeShareShow?: (modal: any) => void;
  rightNode?: React.ReactNode;
  showBackHomeIcon?: boolean;
  backHomeIconClick?: (e, cb?) => void;
  headerBottomSlot?: React.ReactNode;
  contentBottomSlot?: React.ReactNode;
  statusBarColor?: string;
  stickyParams?: {
    custom?: boolean;
    scrollHeight?: number;
    targetRef?: any;
    cb?: (sticky?: boolean) => void;
  };
  //在服务端渲染时, 如果能拿到url上的参数, 可以优化状态栏等
  appStatusBarHeight?: number;
  showBackIcon?: boolean;
  headerBoxClass?: string;
  headerBoxStyle?: React.CSSProperties;
  headerBottomDom?: React.ReactNode;
  backIcon?: React.ReactNode;
  headerHasDivider?: boolean;
  blockStreamlinedHeader?: boolean;
  blockStreamlinedHeaderBackPageHeader?: boolean;
  canUsePageNavHeader?: boolean;
  pageNavHeaderWrapperClassOverwrite?: string;
  hideBackNavigationInPageNavHeader?: boolean;
  pageNavRightNode?: ReactNode;
  streamlinedHeaderPromoBannerRef?: React.RefObject<HTMLDivElement>;
  pageHeaderTextColorOverwrite?: string;
  skipSubSectionStickyForRoute?: boolean;
  //这是header组件的style并非title的style
  titleStyles?: CSSProperties;
  //这是title文字的className
  titleContentStyle?: CSSProperties;
  oldHeaderOverlayPortion?: React.ReactNode;
  oldHeaderMainCTA?: React.ReactNode;
  oldHeaderMainCTAInlineWithBack?: React.ReactNode;
  stickyBoxNoBoxShadow?: boolean;
  // 最外层的data testid
  dataTestId?: string;
  pageNavHeaderCaratSize?: number;
}

export default function Layout(props: Props) {
  const {
    className,
    style,
    headerClassName,
    headerWrapClassName,
    contentClassName,
    contentStyle,
    children,
    header,
    topNode,
    headerBottomSlot,
    contentBottomSlot,
    statusBarColor,
    appStatusBarHeight,
    headerBoxClass,
    headerBoxStyle,
    headerBottomDom,
    headerHasDivider = true,
    blockStreamlinedHeader,
    blockStreamlinedHeaderBackPageHeader = false,
    canUsePageNavHeader,
    pageNavHeaderWrapperClassOverwrite,
    hideBackNavigationInPageNavHeader,
    title,
    pageNavRightNode,
    rightNode,
    streamlinedHeaderPromoBannerRef,
    pageHeaderTextColorOverwrite,
    skipSubSectionStickyForRoute,
    stickyBoxNoBoxShadow,
    dataTestId,
    pageNavHeaderCaratSize,
    ...rest
  } = props;

  const globalNavHeader = useGlobalNavHeader();
  const { t } = useTranslation('common');

  const headerRef = useRef(null);

  const { custom = false, targetRef = headerRef, ...others } = props.stickyParams || {};

  const { eleRef } = useSticky({
    targetRef,
    skipSubSectionStickyForRoute: skipSubSectionStickyForRoute,
    ...others
  });

  const [statusBarHeight, setStatusBarHeight] = useState(appStatusBarHeight);

  const { theme } = useTheme() || {};

  useEffect(() => {
    if (appStatusBarHeight && statusBarHeight !== appStatusBarHeight) {
      setStatusBarHeight(appStatusBarHeight);
    }
  }, [appStatusBarHeight]);

  const __renderTopNode = () => {
    return topNode || null;
  };

  const streamlinedHeader = blockStreamlinedHeader ? false : globalNavHeader?.streamlinedHeader;
  const __fallbackEnabledPageNavHeaderEnabled =
    !blockStreamlinedHeaderBackPageHeader && !isSayWeeeApp() && streamlinedHeader && ENKI_DEFAULTS_TO_NEW_HEADER;
  const __localCanUsePageNavHeader = __fallbackEnabledPageNavHeaderEnabled || canUsePageNavHeader;
  const headerNavSizesConfig = globalNavHeader?.headerNavSizesConfig;

  const boxShadowHook = useScrollToAddBoxShadow({
    targetRef: stickyBoxNoBoxShadow ? undefined : streamlinedHeader ? headerRef : targetRef,
    streamlinedHeader: streamlinedHeader,
    headerSizesConfig: headerNavSizesConfig,
    promoBannerRef: streamlinedHeaderPromoBannerRef
  });
  return (
    <div
      data-testid={dataTestId}
      className={twMerge('bg-surface-200-bg min-h-screen md:w-[750px] md:mx-auto md:my-0', className)}
      id="layout-content-simple"
      style={style}>
      {!streamlinedHeader && __renderTopNode()}
      <div
        className={twMerge('sticky z-[302] top-0 left-0 right-0', !streamlinedHeader ? headerBoxClass : '')}
        style={{
          ...headerBoxStyle
        }}
        id="layout-header"
        ref={headerRef}>
        {statusBarHeight > 0 && <div style={{ height: statusBarHeight, backgroundColor: statusBarColor }} />}
        {streamlinedHeader && __renderTopNode()}
        {!streamlinedHeader && header !== false && (
          <div
            className={twMerge(
              'flex items-center justify-center h-12 max-w-[750px] md:w-[750px] md:mx-auto md:my-0',
              headerWrapClassName
            )}>
            {header ? (
              header
            ) : (
              <Header
                {...rest}
                style={{ backgroundColor: theme?.header?.bg_color }}
                rightNode={!!rightNode ? rightNode : undefined}
                title={title}
                className={twMerge(headerClassName)}
              />
            )}
          </div>
        )}
        {streamlinedHeader && <GlobalNavHeader isSearchMode={false} hasDivider={headerHasDivider} />}
        {!streamlinedHeader && !!headerBottomSlot && headerBottomSlot}
      </div>
      {!streamlinedHeader && headerBottomDom}
      {!!children && (
        <div className={contentClassName} style={contentStyle} data-role="content" ref={boxShadowHook.eleRef}>
          {!!__localCanUsePageNavHeader && (
            <PageNavHeader
              currentPageTitle={title}
              backTitle={t('generic_back_nav_short')}
              pageNavHeaderWrapperClassOverwrite={pageNavHeaderWrapperClassOverwrite}
              hideBack={hideBackNavigationInPageNavHeader || false}
              rightNode={pageNavRightNode ? pageNavRightNode : rightNode ? rightNode : undefined}
              headerColor={statusBarColor}
              textColorOverwriteClass={pageHeaderTextColorOverwrite}
              oldHeaderOverlayPortion={props?.oldHeaderOverlayPortion}
              oldHeaderMainCTA={props?.oldHeaderMainCTA}
              oldHeaderMainCTAInlineWithBack={props?.oldHeaderMainCTAInlineWithBack}
              caratSize={pageNavHeaderCaratSize}
              onBack={props?.onBack}
            />
          )}
          {!custom && <div ref={eleRef} />}
          {children}
        </div>
      )}
      {!!contentBottomSlot && contentBottomSlot}
    </div>
  );
}
